import type { TierStatus } from "./types";

/** Which date the countdown chip counts down to. */
export type CountdownSource = "cycle" | "grace" | "freeze";

/**
 * Which in-widget message bar renders under the card, if any.
 *
 * The `tier*` kinds and `grace` are driven by the latest notification (a
 * transition), not by `TierStatus` (a steady state), so they are not part of
 * `STATUS_PRESENTATION`. `qualificationCountdown` is derived in `index.tsx`
 * rather than by this table — it fires for only some `maintaining` partners
 * (the ones inside the final stretch of the cycle), so a status-keyed table
 * would have to lie about the other `maintaining` partners.
 */
export type NotificationKind =
  | "event"
  | "grace"
  | "completeProfile"
  | "tierUpgrade"
  | "tierDowngrade"
  | "tierFreeze"
  | "tierUnfreeze"
  | "qualificationCountdown"
  | "saveDefaultError";

/**
 * The bar only shows once the qualifying cycle has fewer than this many days
 * left (the boundary itself is excluded — 10 days out shows the chip but not
 * the bar). Product rule from spec Q2 ("if its below 10days"), not a design
 * measurement — named so it isn't a bare `10` in a JSX condition.
 */
export const QUALIFICATION_COUNTDOWN_THRESHOLD_DAYS = 10;

export const isQualificationAtRisk = (
  status: TierStatus,
  rawDays: number | null,
): boolean =>
  status === "maintaining" &&
  rawDays !== null &&
  rawDays >= 1 &&
  rawDays < QUALIFICATION_COUNTDOWN_THRESHOLD_DAYS;

export type StatusPresentation = {
  /** Key into `useRebateTierTranslatedContent()`'s progress headings. */
  headingKey: "progressToUpgrade" | "progressToMaintain" | "yourPerformance";
  /** Whether the criteria progress section renders at all. */
  showProgress: boolean;
  /** `performance` drops the bars, targets and cycle countdown: current values only. */
  criteriaView: "progress" | "performance";
  /** The upgrade affordance (double chevron) beside the heading. */
  showUpgradeAffordance: boolean;
  countdownFrom: CountdownSource | null;
};

/**
 * `TierStatus` → presentation, as one table rather than conditionals spread
 * across the section components. Five statuses against five presentation axes
 * is where state-machine bugs live, and a table is directly enumerable by the
 * Storybook stories.
 *
 * The `tierVisible === false` curated view is a separate axis: `TierHeader`
 * renders only the rebate and its "View details" button, and `index.tsx`
 * suppresses the progress section and every tier notification regardless of
 * status.
 */
export const STATUS_PRESENTATION: Record<TierStatus, StatusPresentation> = {
  upgrading: {
    headingKey: "progressToUpgrade",
    showProgress: true,
    criteriaView: "progress",
    showUpgradeAffordance: true,
    countdownFrom: "cycle",
  },
  maintaining: {
    headingKey: "progressToMaintain",
    showProgress: true,
    criteriaView: "progress",
    showUpgradeAffordance: false,
    countdownFrom: "cycle",
  },
  grace: {
    headingKey: "progressToMaintain",
    showProgress: true,
    criteriaView: "progress",
    showUpgradeAffordance: false,
    countdownFrom: "grace",
  },
  frozen: {
    headingKey: "yourPerformance",
    showProgress: true,
    criteriaView: "performance",
    showUpgradeAffordance: false,
    countdownFrom: "freeze",
  },
  fraudulent: {
    headingKey: "progressToMaintain",
    showProgress: false,
    criteriaView: "progress",
    showUpgradeAffordance: false,
    countdownFrom: null,
  },
};

export const resolveStatusPresentation = (
  status: TierStatus,
  isFinalTier: boolean,
): StatusPresentation => {
  const presentation = STATUS_PRESENTATION[status];

  return isFinalTier && presentation.criteriaView === "progress"
    ? {
        ...presentation,
        headingKey: "progressToMaintain",
        showUpgradeAffordance: false,
      }
    : presentation;
};
